import { useState, useEffect, useRef } from 'react';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 md:h-[4.5rem]">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2 shrink-0">
            <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
              <span className="text-background-50 font-bold text-sm">OB</span>
            </div>
            <span className={`font-heading font-semibold text-base md:text-lg transition-colors ${
              scrolled ? 'text-foreground-950' : 'text-background-50'
            }`}>
              oopbuyspread.bond
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1" ref={dropdownRef}>
            <a
              href="/"
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                scrolled
                  ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                  : 'text-background-100 hover:text-background-50 hover:bg-white/10'
              }`}
            >
              Home
            </a>

            {/* Categories Dropdown with Images */}
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-1 ${
                  scrolled
                    ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                    : 'text-background-100 hover:text-background-50 hover:bg-white/10'
                }`}
              >
                Kategorien
                <i className={`ri-arrow-down-s-line transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[520px] bg-background-50 rounded-xl border border-background-200/70 overflow-hidden z-50 shadow-lg">
                  <div className="p-3">
                    <div className="text-xs font-semibold text-foreground-500 uppercase tracking-wider px-2 pb-2">
                      Alle Kategorien
                    </div>
                    <div className="grid grid-cols-2 gap-1">
                      {categories.map((cat) => (
                        <a
                          key={cat.id}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors group"
                          onClick={() => setDropdownOpen(false)}
                        >
                          <div className="w-10 h-10 rounded-lg overflow-hidden bg-background-200 shrink-0">
                            <img
                              src={cat.iconImg}
                              alt={cat.nameDe}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                            />
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-foreground-950 text-sm truncate">{cat.nameDe}</div>
                            <div className="text-xs text-foreground-500 truncate">{cat.name}</div>
                          </div>
                          <i className="ri-arrow-right-s-line text-foreground-400 text-xs ml-auto shrink-0 group-hover:translate-x-0.5 transition-transform"></i>
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <a
              href="/article/was-ist-oopbuy-spreadsheet"
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                scrolled
                  ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                  : 'text-background-100 hover:text-background-50 hover:bg-white/10'
              }`}
            >
              Guide
            </a>
            <a
              href="/article/oopbuy-anfaenger-guide"
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                scrolled
                  ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                  : 'text-background-100 hover:text-background-50 hover:bg-white/10'
              }`}
            >
              Einkaufsführer
            </a>
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-2">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 rounded-full text-sm font-medium bg-background-50 text-foreground-950 hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              OOPBuy Spreadsheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 rounded-full text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-md"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <i className={`ri-menu-line text-xl ${scrolled ? 'text-foreground-950' : 'text-background-50'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 max-h-[75vh] overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            <a
              href="/"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground-700 hover:bg-background-100 text-sm font-medium"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                <i className="ri-home-4-line text-primary-600"></i>
              </span>
              Home
            </a>

            <a
              href="/article/was-ist-oopbuy-spreadsheet"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground-700 hover:bg-background-100 text-sm font-medium"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center shrink-0">
                <i className="ri-book-open-line text-accent-600"></i>
              </span>
              Guide
            </a>

            <a
              href="/article/oopbuy-anfaenger-guide"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground-700 hover:bg-background-100 text-sm font-medium"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center shrink-0">
                <i className="ri-compass-3-line text-secondary-600"></i>
              </span>
              Einkaufsführer
            </a>

            {/* Collapsible Categories */}
            <div>
              <button
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100"
              >
                <span className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                    <i className="ri-grid-line text-primary-600"></i>
                  </span>
                  Kategorien
                </span>
                <i className={`ri-arrow-down-s-line transition-transform ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {mobileCatOpen && (
                <div className="grid grid-cols-2 gap-1 px-4 pb-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:text-foreground-950 hover:bg-background-100 group"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <div className="w-8 h-8 rounded-md overflow-hidden bg-background-200 shrink-0">
                        <img
                          src={cat.iconImg}
                          alt={cat.nameDe}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                      </div>
                      <span className="truncate text-xs">{cat.nameDe}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div className="pt-4 flex flex-col gap-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="block text-center px-4 py-3 rounded-full text-sm font-medium bg-background-100 text-foreground-950"
              >
                OOPBuy Spreadsheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="block text-center px-4 py-3 rounded-full text-sm font-medium bg-primary-500 text-background-50"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}